import { Navigate, useLocation } from 'react-router-dom';
import { useMe } from '../../hooks/useAuth';
import { useAuthStatus } from '../../hooks/useAuthStatus';
import { RouteLoadingFallback } from '../common/RouteLoadingFallback';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
  skipOnboardingCheck?: boolean;
}

// 온보딩이 필요 없는 경로들
const ONBOARDING_PATHS = ['/onboarding/goal', '/onboarding/level-test', '/character/create'];

/**
 * 인증이 필요한 라우트를 보호하는 컴포넌트
 * + 온보딩 미완료 시 온보딩 플로우로 리다이렉트
 */
export default function ProtectedRoute({
  children,
  requireAuth = true,
  redirectTo = '/login',
  skipOnboardingCheck = false,
}: ProtectedRouteProps) {
  // accessToken 존재 여부가 아니라 만료(exp) 여부까지 확인 (2주 전 로그인이 유지되는 것처럼 보이는 버그 수정)
  const authStatus = useAuthStatus();
  const isAuthenticated = authStatus === 'authenticated';
  const isCheckingAuth = authStatus === 'refreshing';
  const location = useLocation();

  // requireAuth가 false인 페이지(로그인 페이지 등)에서는 useMe 호출 안 함
  const shouldFetchUser = requireAuth && isAuthenticated;
  const { data: user, isLoading } = useMe(shouldFetchUser);

  // accessToken 만료 → refreshToken으로 재발급 시도 중. 확정되기 전까지는
  // 로그인/홈 어느 쪽도 렌더링하지 않고 대기한다 (만료 토큰으로 "로그인된 척" 방지).
  if (isCheckingAuth) {
    return <RouteLoadingFallback fullScreen />;
  }

  // 인증이 필요한 페이지인데 로그인하지 않은 경우
  if (requireAuth && !isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  // 인증이 필요하지 않은 페이지인데 이미 로그인한 경우 (로그인/회원가입 페이지 등)
  if (!requireAuth && isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  // 온보딩 체크가 필요한 경우 (인증된 사용자)
  if (requireAuth && isAuthenticated && !skipOnboardingCheck) {
    // 현재 온보딩 경로에 있으면 체크 스킵
    const isOnboardingPath = ONBOARDING_PATHS.some(path => location.pathname.startsWith(path));

    if (!isOnboardingPath && user) {
      const onboardingStep = user.onboardingStep ?? 0;

      // 온보딩 미완료 시 해당 단계로 리다이렉트
      // 순서: 목표선택(1) → 프로필입력(2) → 레벨테스트(3) → 완료
      if (onboardingStep === 0) {
        return <Navigate to="/onboarding/goal" replace />;
      }

      if (onboardingStep === 1) {
        return <Navigate to="/character/create" replace />;
      }

      if (onboardingStep === 2) {
        return <Navigate to="/onboarding/level-test" replace />;
      }
    }

    // 유저 정보 로딩 중이면 잠시 대기
    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
          <div className="w-8 h-8 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }
  }

  return <>{children}</>;
}
